import clsx from 'clsx';
import type { MultiPolygon as GeoJsonMultiPolygon, Polygon as GeoJsonPolygon } from 'geojson';
import { type ComponentProps, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { computeBoundsFromGeometries, computeViewBox, createCoordinateTransformer } from '~/utils';
import type { Bounds, CoordinateTransformer } from '~types';
import { type UseZoomPanProps, useZoomPan } from '../hooks/useZoomPan';
import { SvgContext, type SvgContextValue } from './SvgContext';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

/** Geometry types accepted by the canvas for bounding box calculation */
type CanvasGeometry = GeoJsonPolygon | GeoJsonMultiPolygon;

type SvgCanvasProps = Omit<ComponentProps<'svg'>, 'ref'> & {
  /**
   * Geometries used to compute the combined viewBox bounding box.
   * The viewBox will encompass all provided geometries with padding.
   * At least one geometry is required for the canvas to render.
   */
  boundedByGeometries: CanvasGeometry[];
  /** Padding factor applied to each side of the bounding box (default: 0.05 = 5%) */
  padding?: number;
  /** Zoom configuration */
  zoom?: UseZoomPanProps['zoom'];
  /** Whether zoom/pan is enabled */
  zoomEnabled?: boolean;
  /**
   * Components rendered as absolutely-positioned overlays on top of the SVG.
   * Use for components that need to stay fixed on screen regardless of zoom/pan
   * (e.g., `<SvgScaleLegend>`). These render outside the `<svg>` element but
   * inside the `SvgContext` provider, so they can access bounds and transform.
   */
  overlay?: React.ReactNode;
  /** Accessible title for the SVG */
  title?: string;
  /** Optional longer description for complex visualizations */
  description?: string;
  /** Ref to the SvgCanvas imperative handle */
  ref?: React.Ref<SvgCanvasRef>;
};

type SvgCanvasRef = {
  /** Reference to the SVG element */
  svg: SVGSVGElement | null;
  /** Reset zoom to identity */
  resetZoom: () => void;
  /** Computed bounds (unpadded) */
  bounds: Bounds | null;
  /** Coordinate transformer */
  transformer: CoordinateTransformer | null;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const DEFAULT_PADDING = 0.05;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/**
 * Compute canvas data (bounds, transformer, viewBox) from an array of polygon geometries.
 * @returns Computed canvas data, or null if no valid geometries are provided
 */
const computeCanvasLayout = (geometries: CanvasGeometry[], padding: number) => {
  const bounds = computeBoundsFromGeometries(geometries);
  if (!bounds) return null;

  return {
    bounds,
    transformer: createCoordinateTransformer(bounds),
    viewBox: computeViewBox(bounds, padding),
  };
};

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

/** Renders the SVG children, optionally wrapped in a zoom/pan transform group */
const SvgCanvasContent = ({
  zoomEnabled,
  transformString,
  children,
}: {
  /** Whether zoom/pan is enabled */
  zoomEnabled: boolean;
  /** CSS transform string for the zoom group */
  transformString: string;
  /** SVG child elements to render */
  children: React.ReactNode;
}) => {
  if (!zoomEnabled) return <>{children}</>;

  return (
    <g className="zoom-group" transform={transformString}>
      {children}
    </g>
  );
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Declarative SVG container with zoom/pan support.
 *
 * Accepts an array of `geometries` to compute a combined viewBox that encompasses
 * all provided polygons. Children receive rendering context (transformer, bounds,
 * viewBox, transform) via `useSvgContext()`.
 *
 * SVG content goes in `children` (rendered inside the zoom/pan group).
 * HTML overlays (e.g., scale legend) go in the `overlay` prop (rendered outside
 * the `<svg>` but inside `SvgContext`).
 *
 * @example
 * ```tsx
 * <SvgCanvas
 *   boundedByGeometries={geometries}
 *   overlay={<SvgScaleLegend />}
 *   title="Blast visualization"
 * >
 *   <SvgGrid />
 *   <DrillHolesLayer holes={drillHoles} />
 * </SvgCanvas>
 * ```
 */
const SvgCanvas = ({
  boundedByGeometries,
  padding = DEFAULT_PADDING,
  zoom,
  zoomEnabled = true,
  overlay,
  title,
  description,
  ref,
  ...rest
}: SvgCanvasProps) => {
  const { className, children, viewBox, ...svgProps } = rest;
  const svgRef = useRef<SVGSVGElement | null>(null);

  // Reactive SVG ref so context consumers re-render when the element mounts
  const [svg, setSvg] = useState<SVGSVGElement | null>(null);
  const setRef = useCallback((node: SVGSVGElement | null) => {
    svgRef.current = node;
    setSvg(node);
  }, []);

  const layout = useMemo(() => computeCanvasLayout(boundedByGeometries, padding), [boundedByGeometries, padding]);
  const { transform, transformString, resetZoom } = useZoomPan({ svgRef, zoom, enabled: zoomEnabled });

  const resolvedViewBox = viewBox ?? layout?.viewBox;

  useImperativeHandle(
    ref,
    () => ({
      svg: svgRef.current,
      resetZoom,
      bounds: layout?.bounds ?? null,
      transformer: layout?.transformer ?? null,
    }),
    [resetZoom, layout],
  );

  const ctxValue = useMemo<SvgContextValue>(
    () => ({
      svg,
      bounds: layout?.bounds ?? null,
      transformer: layout?.transformer ?? null,
      viewBox: resolvedViewBox ?? null,
      transform,
    }),
    [svg, layout, resolvedViewBox, transform],
  );

  return (
    <SvgContext value={ctxValue}>
      <div className="relative h-full w-full">
        <svg
          ref={setRef}
          viewBox={resolvedViewBox}
          className={clsx('h-full w-full', className)}
          role={title ? 'img' : undefined}
          aria-label={title}
          aria-hidden={title ? undefined : true}
          {...svgProps}
        >
          {title && <title>{title}</title>}
          {description && <desc>{description}</desc>}

          <SvgCanvasContent zoomEnabled={zoomEnabled} transformString={transformString}>
            {children}
          </SvgCanvasContent>
        </svg>

        {/* Overlays go here so they can access SvgContext but aren't affected by SVG zoom/pan transforms */}
        {overlay}
      </div>
    </SvgContext>
  );
};
SvgCanvas.displayName = 'SvgCanvas';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { SvgCanvasProps, SvgCanvasRef };
export { SvgCanvas };
